import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { theme } from 'assets/styles/theme';
import { Text1, Text1Bold } from 'components/typography/Texts';
import { LoadingIndicator } from 'components/ui-elements/LoadingIndicator';
import { OpacityOverlay } from 'components/ui-elements/OpacityOverlay';
import { useOrderConfig, usePaymentConfig } from 'providers/cache/cachedDataHooks';
import { PurchaseDto } from 'services/order/orderService.dto';
import { formatMoneyDecimal } from 'utils/numberFormatter';

import FeeSlider from './FeeSlider';
import PriceInput from './PriceInput';

const Container = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 12px;
  padding-bottom: 15px;

  ${theme.mq.phone} {
    flex-direction: column;
  }
`;

const SectionContainer = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 12px;
  gap: 6px;
  border: 1px solid ${theme.color.gray};
  border-radius: 6px;
`;

const PriceSection = styled(SectionContainer)`
  width: 264px;
  height: 99px;
  flex: none;

  ${theme.mq.phone} {
    width: 100%;
  }
`;

const FeeSection = styled(SectionContainer)`
  width: 444px;
  height: 99px;
  flex: 1;

  ${theme.mq.phone} {
    width: 100%;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 0;
  gap: 53px;
  width: 100%;
  height: 24px;
`;

const Label = styled(Text1)`
  color: ${theme.color.darkerGray2};
`;

const InputContainer = styled.div`
  width: 100%;
  height: 45px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0;

  input {
    background: ${theme.color.white} !important;
  }
`;

const SliderContainer = styled.div`
  position: relative;
  width: 100%;
  height: 45px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const SliderWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 22px;
  margin-top: 24px;
  padding: 0 12px;
`;

interface Props {
  onFeeChange: (fee: number) => void;
  onPriceChange: (price: number) => void;
  isProcessing: boolean;
  purchase?: PurchaseDto;
}

const PriceAndFeeSelection = ({ onFeeChange, onPriceChange, purchase, isProcessing }: Props) => {
  const { t } = useTranslation('checkout');
  const { orderConfig } = useOrderConfig();
  const { paymentConfig } = usePaymentConfig();

  const displayFee = !!purchase;
  const displayPrice = !!purchase && !!orderConfig && !!paymentConfig;

  return (
    <Container>
      <PriceSection>
        <HeaderRow>
          <Label>{t('price.price')}</Label>
          {displayPrice && <Text1Bold>{formatMoneyDecimal(purchase.basePrice)}</Text1Bold>}
        </HeaderRow>
        <InputContainer>
          {!displayPrice && <LoadingIndicator />}
          {displayPrice && (
            <PriceInput
              minPrice={orderConfig.minBuyerBasePrice}
              maxPrice={paymentConfig.maxItemPrice}
              onChange={onPriceChange}
              placeholder={t('price.placeholder')}
              isDisabled={isProcessing}
            />
          )}
        </InputContainer>
      </PriceSection>

      <FeeSection>
        <HeaderRow>
          <Label>{t('price.fee')}</Label>
          {displayFee && <Text1Bold>{formatMoneyDecimal(purchase.buyerTransactionFee)}</Text1Bold>}
        </HeaderRow>
        <SliderContainer>
          {!displayFee && <LoadingIndicator />}
          {displayFee && purchase && (
            <SliderWrapper>
              <FeeSlider
                buyerFee={purchase.buyerTransactionFee}
                sellerFee={purchase.sellerTransactionFee}
                minFeePercent={purchase.minBuyerFeePercent}
                onChange={onFeeChange}
              />
            </SliderWrapper>
          )}
        </SliderContainer>
      </FeeSection>
      {isProcessing && <OpacityOverlay />}
    </Container>
  );
};

export default PriceAndFeeSelection;
